/**
 * useMoneyExcludedJobs.js — Per-user job exclusions for the Jobs money counter
 *
 * All jobs are included by default; toggling excludes/re-includes a single job
 * (atomic single-id PUT, like pinning). State lives in the shared React Query
 * cache so every consumer (counter strip, JobList card checkboxes) updates
 * optimistically and in lock-step from one source of truth.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { api } from '../lib/api';

const QK = ['money-excluded-jobs'];

// Toggles in flight across all hook instances. While >1, skip applying server
// responses (they may arrive out of order); the optimistic cache already
// matches the server's cumulative single-id semantics.
let inflightCount = 0;

export function useMoneyExcludedJobs() {
  const { profile } = useAuth();
  const userId = profile?.id;
  const qc = useQueryClient();

  const { data: excludedIds = [] } = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const me = await api.get('/auth/me');
      return Array.isArray(me?.money_excluded_jobs) ? me.money_excluded_jobs : [];
    },
    enabled: !!userId,
    staleTime: Infinity, // single-admin state; mutations below keep it fresh
  });

  const isExcluded = useCallback((jobId) => excludedIds.includes(jobId), [excludedIds]);

  const toggleExcluded = useCallback(async (jobId) => {
    if (!userId) return;
    const cur = qc.getQueryData(QK) || [];
    const wasExcluded = cur.includes(jobId);

    // Optimistic shared-cache update — all consumers re-render immediately.
    qc.setQueryData(QK, wasExcluded ? cur.filter((id) => id !== jobId) : [...cur, jobId]);

    inflightCount++;
    try {
      const res = await api.put(`/profiles/${userId}/money-excluded`, { jobId, excluded: !wasExcluded });
      if (inflightCount === 1 && Array.isArray(res?.money_excluded_jobs)) {
        qc.setQueryData(QK, res.money_excluded_jobs);
      }
    } catch (e) {
      console.error('Failed to update money counter exclusions:', e);
      qc.invalidateQueries({ queryKey: QK });
    } finally {
      inflightCount--;
    }
  }, [userId, qc]);

  return { excludedIds, isExcluded, toggleExcluded };
}
